import { useEffect, useState } from 'react';

import type {
  Master,
  MasterChanges,
  MasterCycleInput,
  NewMaster,
  OperatorOption,
} from '../../api/monitor';
import { comesFromSgc, cycleStartDay, formatCnpj } from '../../lib/masters';

interface CycleField {
  enabled: boolean;
  billEndDay: string;
  contractNumber: string;
}

interface MasterModalProps {
  /** Nula para cadastrar uma master nova. */
  master: Master | null;
  operators: OperatorOption[];
  saving: boolean;
  onClose: () => void;
  onCreate: (payload: NewMaster) => Promise<void>;
  onUpdate: (changes: MasterChanges) => Promise<void>;
}

/**
 * Cadastro e edição de master, com o ciclo de faturamento de cada operadora.
 *
 * O ciclo é por operadora porque é do contrato da master com ela — a mesma
 * master vira o ciclo em dias diferentes na Claro e na Vivo. O dia informado é
 * o de fechamento (como o SGC guarda); o ciclo abre no dia seguinte.
 *
 * Na master que vem do SGC, nome, CNPJ, código e situação voltam ao que o SGC
 * diz na próxima sincronização de masters; o ciclo não, porque o snapshot não
 * o traz. O aviso no formulário diz isso.
 */
export function MasterModal({
  master,
  operators,
  saving,
  onClose,
  onCreate,
  onUpdate,
}: MasterModalProps) {
  const creating = master === null;
  const [name, setName] = useState(master?.name ?? '');
  const [cnpj, setCnpj] = useState(master ? formatCnpj(master.cnpj) : '');
  const [code, setCode] = useState(master?.code ?? '');
  const [active, setActive] = useState(master?.active ?? true);
  const [cycles, setCycles] = useState<Record<string, CycleField>>(() =>
    Object.fromEntries(
      operators.map((operator) => {
        const cycle = master?.cycles.find(
          (item) => item.operatorCode === operator.code
        );

        return [
          operator.code,
          {
            enabled: Boolean(cycle),
            billEndDay: cycle ? String(cycle.billEndDay) : '',
            contractNumber: cycle?.contractNumber ?? '',
          },
        ];
      })
    )
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const setCycle = (operatorCode: string, field: Partial<CycleField>) =>
    setCycles((current) => ({
      ...current,
      [operatorCode]: { ...current[operatorCode], ...field },
    }));

  /** Os ciclos como a API espera; desmarcado = remover o ciclo da operadora. */
  const cycleInputs = (): MasterCycleInput[] | string => {
    const inputs: MasterCycleInput[] = [];

    for (const operator of operators) {
      const field = cycles[operator.code];

      if (!field?.enabled) {
        inputs.push({ operatorCode: operator.code, billEndDay: null });
        continue;
      }

      const day = Number(field.billEndDay);
      if (!Number.isInteger(day) || day < 1 || day > 31) {
        return `Informe o dia de fechamento do ciclo ${operator.name} (1 a 31).`;
      }

      inputs.push({
        operatorCode: operator.code,
        billEndDay: day,
        contractNumber: field.contractNumber.trim() || null,
      });
    }

    return inputs;
  };

  const handleSubmit = async () => {
    const inputs = cycleInputs();

    if (typeof inputs === 'string') {
      setError(inputs);
      return;
    }

    if (!name.trim() || cnpj.replace(/\D/g, '').length !== 14) {
      setError('Preencha o nome e o CNPJ (14 dígitos) da master.');
      return;
    }

    if (!inputs.some((input) => input.billEndDay !== null)) {
      setError('Informe o ciclo de faturamento de pelo menos uma operadora.');
      return;
    }

    setError(null);

    try {
      if (creating) {
        await onCreate({
          name: name.trim(),
          cnpj,
          code: code.trim(),
          cycles: inputs,
        });
      } else {
        await onUpdate({
          name: name.trim(),
          cnpj,
          code: code.trim(),
          active,
          cycles: inputs,
        });
      }
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  const input =
    'border-line-300 focus:border-navy-800 rounded-[10px] border p-3 text-[14px] outline-none focus:ring-2 focus:ring-[#00244733]';

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 bg-[rgb(10_20_45/0.45)]"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={creating ? 'Nova master' : `Editar master ${master.name}`}
        className="animate-rise-fast fixed top-1/2 left-1/2 z-[51] max-h-[88dvh] w-[min(560px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-[18px] bg-white p-[22px]"
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="text-[16px] font-extrabold">
            {creating ? 'Nova master' : 'Editar master'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-400 hover:text-ink-900 p-1 text-[20px] leading-none"
          >
            ×<span className="sr-only">Fechar</span>
          </button>
        </div>

        {!creating && comesFromSgc(master) ? (
          <div className="bg-warn-100 text-warn-700 mb-3 rounded-[10px] px-3 py-2.5 text-[12px] leading-relaxed">
            Esta master vem do SGC (id {master.externalId}). Nome, CNPJ, código
            e situação voltam ao que o SGC diz na próxima sincronização de
            masters — corrija lá. Os ciclos ficam como salvos aqui.
          </div>
        ) : null}

        <div className="flex flex-col gap-2.5">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nome da master"
            aria-label="Nome da master"
            className={input}
          />
          <div className="flex gap-2.5">
            <input
              value={cnpj}
              onChange={(event) => setCnpj(event.target.value)}
              placeholder="CNPJ (00.000.000/0000-00)"
              aria-label="CNPJ"
              inputMode="numeric"
              className={`${input} min-w-0 flex-1`}
            />
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Código (opcional)"
              aria-label="Código"
              maxLength={10}
              className={`${input} w-[150px]`}
            />
          </div>

          <div className="text-ink-600 mt-1 text-[12px] font-bold">
            Ciclo de faturamento por operadora
          </div>

          {operators.map((operator) => {
            const field = cycles[operator.code];
            const day = Number(field?.billEndDay);

            return (
              <div
                key={operator.code}
                className="border-line-300 rounded-[10px] border px-3 py-2.5"
              >
                <label className="flex cursor-pointer items-center gap-2 text-[13.5px] font-bold">
                  <input
                    type="checkbox"
                    checked={field?.enabled ?? false}
                    onChange={(event) =>
                      setCycle(operator.code, { enabled: event.target.checked })
                    }
                    className="size-4"
                  />
                  Contrato com a {operator.name}
                </label>

                {field?.enabled ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2.5">
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={field.billEndDay}
                      onChange={(event) =>
                        setCycle(operator.code, {
                          billEndDay: event.target.value,
                        })
                      }
                      placeholder="Fecha no dia"
                      aria-label={`Dia de fechamento do ciclo ${operator.name}`}
                      className={`${input} w-[140px]`}
                    />
                    <input
                      value={field.contractNumber}
                      onChange={(event) =>
                        setCycle(operator.code, {
                          contractNumber: event.target.value,
                        })
                      }
                      placeholder="Nº do contrato (opcional)"
                      aria-label={`Número do contrato ${operator.name}`}
                      maxLength={30}
                      className={`${input} min-w-0 flex-1`}
                    />
                    <span className="text-ink-600 w-full text-[11.5px]">
                      {Number.isInteger(day) && day >= 1 && day <= 31
                        ? `O ciclo fecha no dia ${day} e vira no dia ${cycleStartDay(day)}.`
                        : 'Informe o dia em que o ciclo fecha (1 a 31).'}
                    </span>
                  </div>
                ) : null}
              </div>
            );
          })}

          {!creating ? (
            <label className="border-line-300 flex cursor-pointer items-center justify-between gap-3 rounded-[10px] border px-3 py-2.5">
              <span>
                <span className="block text-[13.5px] font-bold">
                  Master ativa
                </span>
                <span className="text-ink-600 block text-[11.5px]">
                  Inativa, sai das listas e da escolha de relatórios. Nada é
                  apagado.
                </span>
              </span>
              <input
                type="checkbox"
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                className="size-4 shrink-0"
              />
            </label>
          ) : null}
        </div>

        {error ? (
          <div className="text-danger-600 mt-2.5 text-[12.5px] font-bold">
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border-line-300 text-ink-600 flex-1 rounded-[10px] border bg-white p-3 text-[13.5px] font-extrabold"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="bg-navy-800 hover:bg-navy-950 flex-1 rounded-[10px] p-3 text-[13.5px] font-extrabold text-white disabled:opacity-60"
          >
            {saving ? 'Salvando…' : creating ? 'Cadastrar master' : 'Salvar'}
          </button>
        </div>
      </div>
    </>
  );
}
